import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { useFlags } from '@/hooks/useFlags';
import { AuthGateScreen } from './AuthGateScreen';
import { endSettling, useGateState } from './gateStore';

/** How long the gate may stay up before it says so and offers a retry. */
export const GATE_SLOW_MS = 10_000;
/** The hard stop: a destination that never settles (a lazy chunk that 404s after a deploy, a request that hangs) ends in a message, never an endless spinner. */
export const GATE_STOP_MS = 30_000;

/**
 * ONE loading screen from the moment auth work starts (Sign in / Sign up clicked, back from Google, a cold load with a stored session) until the
 * destination is ready, then one reveal. The app stays MOUNTED but hidden behind the gate, so the redirects, lazy screens, flags and first-run check
 * all happen out of sight and the form keeps what was typed if the sign-in fails. The gate opens when, for two frames in a row: the session is
 * resolved, the flags are in (signed in), nothing behind it holds it (a route still loading, the first-run check) and the router has reached its
 * destination. It never decides where anyone goes and never relaxes a guard: the guards still run, only unseen.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { loading, isAuthenticated } = useAuth();
  const { phase, holds } = useGateState();
  const flags = useFlags(isAuthenticated && phase !== 'idle');
  const flagsPending = isAuthenticated && phase !== 'idle' && flags.isPending;
  const ready = phase === 'navigated' && !loading && !flagsPending && holds === 0;

  // open only after the conditions held for two frames in a row (a Suspense fallback or a redirect mounts a frame after the render that asked for it)
  useEffect(() => {
    if (!ready) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => endSettling());
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [ready]);

  const gating = phase !== 'idle';
  const [slow, setSlow] = useState(false);
  const [stopped, setStopped] = useState(false);
  useEffect(() => {
    if (!gating) {
      setSlow(false);
      setStopped(false);
      return;
    }
    const slowId = window.setTimeout(() => setSlow(true), GATE_SLOW_MS);
    const stopId = window.setTimeout(() => setStopped(true), GATE_STOP_MS);
    return () => {
      window.clearTimeout(slowId);
      window.clearTimeout(stopId);
    };
  }, [gating]);

  return (
    <>
      {/* hidden, not unmounted: display:contents when shown adds no box of its own */}
      <div style={{ display: gating ? 'none' : 'contents' }} aria-hidden={gating || undefined}>
        {children}
      </div>
      {gating && <AuthGateScreen slow={slow} stopped={stopped} onRetry={() => window.location.reload()} />}
    </>
  );
}
